import { createContext, ReactNode, useContext } from 'react';
import { DOMAIN } from '../config';
import { useCurrentUser } from '../queries/use-current-user';
import { User } from '../types/user';
import { useAuthProfileContext } from './AuthProfileProvider';
import * as Sentry from '@sentry/react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const UserContext = createContext<User | undefined>(undefined);

type UserProviderProps = {
    children: ReactNode | ReactNode[];
};

function UserProvider({ children }: UserProviderProps): JSX.Element {
    const profile = useAuthProfileContext();
    const { logout } = useAuth0();

    const userQuery = useCurrentUser(profile[`${DOMAIN}/uuid`]);

    if (userQuery.isLoading) {
        return <>Loading...</>;
    }

    if (userQuery.isError || !userQuery.isSuccess) {
        logout({ logoutParams: { returnTo: window.location.origin } });
    }

    if (!userQuery.data?.user) {
        return <Navigate to="/onboarding" />;
    }

    if (process.env.REACT_APP_MOCK_API !== 'true') {
        Sentry.setUser({ id: userQuery.data.user.id });
    }

    return <UserContext.Provider value={userQuery.data.user}>{children}</UserContext.Provider>;
}

function useUserContext(): User {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserProvider');
    }
    return context;
}

export { UserProvider, useUserContext };
